/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { ShopperBasketsV2 } from '@/scapi';
import { Truck } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { formatCurrency } from '@/lib/currency';

interface CartTitleProps {
    basket: ShopperBasketsV2.schemas['Basket'];
    deliveryCount: number;
    /** Promotion-provided subtotal threshold for free standard shipping. */
    freeShippingThreshold?: number;
    /** Promotion title for display purposes. */
    promotionTitle?: string;
}

export default function CartTitle({ basket, promotionTitle, freeShippingThreshold }: CartTitleProps): ReactElement {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useSite();
    const subtotal = basket?.productSubTotal ?? 0;
    const showFreeShippingProgress =
        typeof freeShippingThreshold === 'number' &&
        freeShippingThreshold > 0 &&
        subtotal < freeShippingThreshold;
    const remainingAmount = showFreeShippingProgress ? freeShippingThreshold - subtotal : 0;
    const progressPercent = showFreeShippingProgress ? Math.min(100, (subtotal / freeShippingThreshold) * 100) : 0;
    const freeShippingMessageKey = promotionTitle?.trim() ? 'delivery.freeShippingRemainingPromo' : 'delivery.freeShippingRemaining';

    return (
        <div className="mb-1">
           {/*  <div className="flex items-center gap-2">
                <Truck className="size-[1.125rem] text-foreground shrink-0" aria-hidden />
                <div>
                    <Typography
                        variant="h5"
                        as="h2"
                        className="text-xl font-normal leading-[120%] tracking-[-0.6px] text-card-foreground">
                        {t('delivery.heading', { deliveryCount, count: totalCount })}
                    </Typography>
                    {formattedAddress && (
                        <p className="text-xs md:text-sm text-muted-foreground mt-1">{formattedAddress}</p>
                    )}
                </div>
            </div> */}
            {showFreeShippingProgress && (
                <div className="mt-1 flex items-start gap-2 rounded-lg border border-[var(--cart-shipping-card-border)] bg-background px-4 py-3">
                    <Truck className="size-[1.5rem] text-primary shrink-0" aria-hidden />
                    <div className="flex flex-1 flex-col gap-2">
                        <p className="font-stjude-bold text-sm">
                            <Trans
                                t={t}
                                i18nKey={freeShippingMessageKey}
                                values={{ amount: formatCurrency(remainingAmount, i18n.language, currency), promotionTitle }}
                                components={{
                                    bold: <span className="font-stjude-bold text-primary" />,
                                    free: <span className="font-stjude-medium uppercase" />,
                                }}
                            />
                        </p>
                        <div className="flex items-center gap-2">
                            <div
                                className="h-2 grow overflow-hidden rounded-full bg-[var(--cart-shipping-track-bg)]"
                                role="progressbar"
                                aria-valuenow={Math.round(progressPercent)}
                                aria-valuemin={0}
                                aria-valuemax={100}>
                                <div
                                    className="h-full rounded-full bg-primary transition-[width]"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <span className="font-stjude-bold text-sm shrink-0 text-right">
                                {formatCurrency(freeShippingThreshold, i18n.language, currency)}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
